const UPLOAD_GATEWAY_TIMEOUT_MESSAGE =
  "Upload timed out before the server finished validating the file. Files with many catalog IDs can take several minutes to validate. Please try again, split the upload into smaller files, or contact support if the issue persists.";

/**
 * Normalizes axios upload failures (including gateway HTML bodies) into a TAP error shape.
 * @param {*} error
 * @returns {Object|null}
 */
export function normalizeUploadErrorResponse(error) {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status == null && data == null) {
    return null;
  }

  if (typeof data === "object" && data != null && !Array.isArray(data)) {
    return {
      ...data,
      httpCode: data.httpCode ?? status,
      message: data.message ?? getUploadErrorMessage({ ...data, httpCode: status }),
      error: data.error ?? true,
    };
  }

  return {
    httpCode: status,
    message: getUploadErrorMessage({ httpCode: status }),
    error: true,
  };
}

/**
 * Returns a user-facing upload error message for HTTP failures.
 * @param {*} response
 * @returns {string}
 */
export function getUploadErrorMessage(response) {
  if (response?.httpCode === 504 || response?.httpCode === 408) {
    return UPLOAD_GATEWAY_TIMEOUT_MESSAGE;
  }

  if (typeof response?.message === "string" && response.message.trim()) {
    return response.message;
  }

  if (typeof response?.error === "string" && response.error.trim()) {
    return response.error;
  }

  if (response?.httpCode) {
    return `Failed to upload licensing job (HTTP ${response.httpCode}).`;
  }

  return "Failed to upload licensing job.";
}

/**
 * Maps bulk ingest validation errors to upload task rows for UploadJobTasksList.
 * @param {*} bulkResult
 * @returns {Array}
 */
export function mapBulkIngestErrorsToUploadTasks(bulkResult) {
  const errors = Array.isArray(bulkResult?.errorMessages)
    ? bulkResult.errorMessages
    : [];

  return errors.map((error, index) => ({
    id: error?.id ?? index,
    taskDetailsId: error?.recordIndex,
    taskDetails: error?.recordGroup || "",
    statusMessage: error?.message || "",
  }));
}

/**
 * Returns a job tracker ID when the upload API responds asynchronously.
 * @param {*} response
 * @returns {string|number|null}
 */
export function getAsyncUploadJobTrackerId(response) {
  if (response == null) {
    return null;
  }

  if (typeof response === "number") {
    return response;
  }

  if (typeof response === "string" && /^\d+$/.test(response)) {
    return response;
  }

  return null;
}

/**
 * Returns true when the response is a standard TAP API error payload.
 * @param {*} response
 * @returns {boolean}
 */
export function isApiErrorResponse(response) {
  if (response == null) {
    return true;
  }

  if (typeof response === "string") {
    return true;
  }

  return Boolean(response.code || response.httpCode || response.error);
}

/**
 * Returns true when a bulk upload response contains validation errors.
 * @param {*} response
 * @returns {boolean}
 */
export function isBulkIngestValidationFailure(response) {
  if (!response || isApiErrorResponse(response)) {
    return false;
  }

  return (
    (response.numErrorRows != null && response.numErrorRows > 0) ||
    (Array.isArray(response.errorMessages) && response.errorMessages.length > 0)
  );
}

/**
 * Formats bulk ingest validation errors for display in a toast.
 * @param {*} response
 * @returns {string}
 */
export function formatBulkIngestValidationErrors(response) {
  const lines = [];

  if (response?.bulkStatus) {
    lines.push(response.bulkStatus);
  }

  const errors = Array.isArray(response?.errorMessages)
    ? response.errorMessages
    : [];

  errors.forEach(({ recordGroup, recordIndex, message }) => {
    const rowLabel = recordGroup
      ? `${recordGroup}${recordIndex != null ? ` (row ${recordIndex})` : ""}`
      : recordIndex != null
        ? `Row ${recordIndex}`
        : "Row";
    lines.push(`${rowLabel}: ${message}`);
  });

  if (lines.length === 0) {
    return "Upload failed due to validation errors.";
  }

  return lines.join("\n");
}
